import Allow from 'components/Allow';
import { useMainContext } from 'context/MainContext';
import { enumRules } from 'enum/enumRules';
import { ICurrency, INumberFormatCurrency } from 'interfaces/ICurrency';
import { IProductPrice } from 'interfaces/IProduct';
import React, { useCallback } from 'react';
import {
  Button,
  Col,
  FormControl,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap';

import { Block, Check } from 'styled-icons/boxicons-regular';
import { masks } from 'utils/masks';
import TableTooltips from '../TableTooltips';

type Props = {
  list: IProductPrice[] | null;
  setProductPriceList: React.Dispatch<
    React.SetStateAction<IProductPrice[] | null>
  >;
  selectedCurrency: ICurrency;
};

const TablePriceListCompany: React.FC<Props> = ({
  list,
  setProductPriceList,
  selectedCurrency
}) => {
  const { setDialog, profile } = useMainContext();
  const productPriceList = list as IProductPrice[];

  const convertCurrency = (
    value: number,
    numberFormat: INumberFormatCurrency
  ): string => {
    const currencyFormatter = new Intl.NumberFormat(numberFormat.Locale, {
      style: 'currency',
      currency: numberFormat.Currency
    });
    return currencyFormatter.format(value);
  };

  const handleNullPriceCost = useCallback(
    (item: IProductPrice, disable: boolean) => {
      setProductPriceList(prevState => {
        const updatedList = prevState?.map(x => {
          if (x.ProductId === item.ProductId) {
            if (item.CompanyId === 1) {
              return {
                ...x,
                CostCompany: disable ? null : x.CostCompany || 0,
                PriceCompany: disable ? null : x.PriceCompany || 0
              };
            }
            return {
              ...x,
              CostCompany: disable
                ? null
                : item.CostCompany || item.CostCompanyDefault,
              PriceCompany: disable
                ? null
                : item.PriceCompany || item.PriceCompanyDefault
            };
          }
          return x;
        });
        return updatedList || [];
      });
    },
    [setProductPriceList]
  );

  const handleActive = useCallback(
    (item: IProductPrice, active: boolean) => {
      const dialogMessage = active
        ? `Ao ativar este produto você poderá definir um custo e preço específico deste para esta companhia.`
        : `Ao desativar, o produto retornará os valores de preço e custo padrões da companhia de origem.`;
      setDialog({
        show: true,
        textBody: dialogMessage,
        callBackConfirm: () => handleNullPriceCost(item, !active),
        callBackCancel: active
          ? undefined
          : () => handleNullPriceCost(item, false)
      });
    },
    [setDialog, handleNullPriceCost]
  );

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      item: IProductPrice,
      field: 'cost' | 'price'
    ) => {
      const value = e.target.value.length ? Number(e.target.value) : null;

      //if (!value) handleActive(item, false);

      setProductPriceList(prevState => {
        return (prevState as IProductPrice[]).map(x =>
          x.ProductId === item.ProductId
            ? {
                ...x,
                [field === 'cost' ? 'CostCompany' : 'PriceCompany']: value
              }
            : x
        );
      });
    },
    // [handleActive, setProductPriceList]
    [setProductPriceList]
  );

  const getFormattedValue = useCallback(
    (value: number | null, defaultValue: number | null) => {
      if (value !== null) {
        return convertCurrency(
          value,
          selectedCurrency?.NumberFormat as INumberFormatCurrency
        );
      }

      if (defaultValue !== null) {
        return convertCurrency(
          defaultValue,
          selectedCurrency?.NumberFormat as INumberFormatCurrency
        );
      }

      return '';
    },
    [selectedCurrency]
  );

  const renderInputGroup = useCallback(
    (productPrice: IProductPrice, field: 'cost' | 'price') => {
      const isCost = field === 'cost';
      const value = isCost
        ? productPrice.CostCompany
        : productPrice.PriceCompany;
      const defaultValue = isCost
        ? productPrice.CostCompanyDefault
        : productPrice.PriceCompanyDefault;

      return (
        <InputGroup>
          <InputGroup.Text>{selectedCurrency?.Symbol || 'R$'}</InputGroup.Text>
          <FormControl
            key={`${field}${productPrice.ProductId}${productPrice.CompanyId}`}
            type="text"
            inputMode="decimal"
            onBlur={e => {
              if (e.target.value.length === 0)
                handleActive(productPrice, false);
            }}
            disabled={value === null}
            value={getFormattedValue(value, defaultValue)}
            onChange={e => {
              masks.currency(e as React.FormEvent<HTMLInputElement>);
              handleChange(
                e as React.ChangeEvent<HTMLInputElement>,
                productPrice,
                field
              );
            }}
          />
        </InputGroup>
      );
    },
    [handleActive, handleChange, selectedCurrency, getFormattedValue]
  );

  const renderRow = useCallback(
    (productPrice: IProductPrice) => {
      return (
        <tr
          style={{ color: productPrice.CostCompany === null ? 'red' : '' }}
          key={`${productPrice.ProductId}${productPrice.CompanyId}`}
        >
          <td>{productPrice.ProductId}</td>
          <td>{productPrice.ProductName}</td>
          <td>{productPrice.ProductInternalName}</td>
          <td>{productPrice.ProductType}</td>
          <td>
            {profile?.Rules?.includes(
              enumRules.ProductPrice.CanWriteProductPrice
            ) || profile?.IsAdm
              ? renderInputGroup(productPrice, 'cost')
              : convertCurrency(
                  (productPrice.CostCompany ||
                    productPrice.CostCompanyDefault) as number,
                  selectedCurrency?.NumberFormat as INumberFormatCurrency
                )}
          </td>
          <td>
            {profile?.Rules?.includes(
              enumRules.ProductPrice.CanWriteProductPrice
            ) || profile?.IsAdm
              ? renderInputGroup(productPrice, 'price')
              : convertCurrency(
                  (productPrice.PriceCompany ||
                    productPrice.PriceCompanyDefault) as number,
                  selectedCurrency?.NumberFormat as INumberFormatCurrency
                )}
          </td>
          <td>
            {/* <Allow ruleId={enumRules.ProductPrice.CanWriteProductPrice}>
              <Button
                variant="outline-primary"
                onClick={() =>
                  handleActive(productPrice, productPrice.CostCompany === null)
                }
              >
                {productPrice.CostCompany === null ? (
                  <Block title="Desativado" size={25} />
                ) : (
                  <Check title="Ativado" size={25} />
                )}
              </Button>
            </Allow> */}
          </td>
        </tr>
      );
    },
    // [handleActive, profile, renderInputGroup, selectedCurrency]
    [profile, renderInputGroup, selectedCurrency]
  );

  return (
    <div>
      {productPriceList && productPriceList.length > 0 && (
        <Row>
          <Col>
            <div className="table-thead-fixed">
              <Table className="table text-center" responsive>
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Nome</th>
                    <th>Nome Interno</th>
                    <th>Tipo</th>
                    <th>
                      Custo Base{' '}
                      <TableTooltips description="Valor faturado real para a Golden." />
                    </th>
                    <th>
                      Preço Base{' '}
                      <TableTooltips description="Preço de venda desta companhia para seus clientes." />
                    </th>
                    <th>
                      {/* <Allow
                        ruleId={enumRules.ProductPrice.CanWriteProductPrice}
                      >
                        <TableTooltips description="Disponibilizar produto para seus clientes." />
                      </Allow> */}
                    </th>
                  </tr>
                </thead>
                <tbody id="tbody-company">
                  {productPriceList.map(renderRow)}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TablePriceListCompany;
